import { z } from "zod/v3";

// ── Input Schema ─────────────────────────────────────────────────────────────

function normalizeWebsite(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withProtocol).toString();
  } catch {
    return undefined;
  }
}

export const GrowthPipelineInputSchema = z.object({
  url: z
    .preprocess(normalizeWebsite, z.string().url())
    .describe("The business website URL to analyze"),
  businessName: z.string().min(1).describe("Business/brand name"),
  industry: z.string().min(1).describe("Industry or vertical"),
  valueProposition: z.string().optional().describe("Core value proposition"),
  businessModel: z.string().optional().describe("Business model (SaaS, ecommerce, services, etc.)"),
});
export type GrowthPipelineInput = z.infer<typeof GrowthPipelineInputSchema>;

// ── Tracking Event Schema ────────────────────────────────────────────────────

export const GrowthTrackingEventSchema = z.object({
  name: z.string().describe("Event name (e.g. sign_up, purchase, add_to_cart)"),
  type: z.enum(["primary", "secondary"]).describe("Conversion priority"),
  triggerType: z
    .enum(["click", "pageview", "form_submit", "custom"])
    .describe("How the event fires"),
  approved: z.boolean().default(true).describe("Whether user approved this event"),
  // Recorder-enriched fields (optional, present when source === 'recorder')
  triggerDetail: z.string().optional().describe("Exact CSS selector, element ID, or form action"),
  reason: z.string().optional().describe("Why this event matters"),
  observedIn: z.string().optional().describe("The recorded interaction that surfaced this"),
  source: z.enum(["scraper", "recorder"]).optional().describe("How the event was discovered"),
});
export type GrowthTrackingEvent = z.infer<typeof GrowthTrackingEventSchema>;

// ── Campaign Schema ──────────────────────────────────────────────────────────

export const GrowthCampaignSchema = z.object({
  type: z.enum(["search", "pmax"]).describe("Google Ads campaign type"),
  name: z.string().describe("Campaign name"),
  budget: z.number().describe("Daily budget in USD"),
  keywords: z.array(z.string()).optional().describe("Target keywords (Search only)"),
  adCopy: z
    .object({
      headlines: z.array(z.string()).describe("Up to 15 headlines"),
      descriptions: z.array(z.string()).describe("Up to 4 descriptions"),
    })
    .optional()
    .describe("Ad copy (Search only)"),
  approved: z.boolean().default(false).describe("Whether user approved this campaign"),
});
export type GrowthCampaign = z.infer<typeof GrowthCampaignSchema>;

// ── Pipeline Steps ───────────────────────────────────────────────────────────

export const GROWTH_PIPELINE_STEPS = [
  { label: "Business Discovery", icon: "settings", service: "User Input" },
  { label: "Google Auth", icon: "lock", service: "OAuth 2.0" },
  { label: "Website Analysis", icon: "globe", service: "Gemini Pro" },
  { label: "Tracking Plan", icon: "bar-chart", service: "Gemini Pro" },
  { label: "GTM & GA4 Setup", icon: "gtm", service: "Google APIs" },
  { label: "Campaign Generation", icon: "google-ads", service: "Gemini Pro" },
  { label: "Campaign Review", icon: "check-circle", service: "User Review" },
  { label: "Deploy & Launch", icon: "zap", service: "Google Ads" },
];
